/**
 * Storage keys for Vezgo integration
 * Centralized storage key management to prevent duplication and ensure consistency
 */
export enum VezgoStorageKeys {
  /**
   * OAuth state parameter for security
   */
  STATE = 'vezgo_state',
  
  /**
   * URL to return to after connection flow completes
   */
  RETURN_URL = 'vezgo_return_url',
  
  /**
   * Currently selected provider
   */
  PROVIDER = 'vezgo_provider',
  
  /**
   * Previous path before starting connection flow
   */
  PREVIOUS_PATH = 'vezgo_previous_path',
  
  /**
   * Flag indicating callback was processed
   */
  CALLBACK_PROCESSED = 'vezgo_callback_processed',
  
  /**
   * Timestamp of last callback
   */
  CALLBACK_TIME = 'vezgo_callback_time',
  
  /**
   * Provider from callback - will be deprecated
   */
  CALLBACK_PROVIDER = 'vezgo_callback_provider',
  
  /**
   * Account ID from callback - new primary identifier
   */
  CALLBACK_ACCOUNT_ID = 'vezgo_callback_account_id',
  
  /**
   * Connection ID from callback
   */
  CALLBACK_CONNECTION_ID = 'vezgo_callback_connection_id',
  
  /**
   * Authentication token
   */
  TOKEN = 'vezgo_token',
  
  /**
   * Last error message
   */
  ERROR = 'vezgo_error'
}

/**
 * Standardized storage utility for Vezgo integration
 * Provides type-safe access to storage operations
 */
export const vezgoStorage = {
  /**
   * Store state parameter for OAuth flow
   */
  setState: (state: string) => sessionStorage.setItem(VezgoStorageKeys.STATE, state),
  
  /**
   * Get stored state parameter
   */
  getState: () => sessionStorage.getItem(VezgoStorageKeys.STATE),
  
  /**
   * Clear stored state parameter
   */
  clearState: () => sessionStorage.removeItem(VezgoStorageKeys.STATE),
  
  /**
   * Store return URL for navigation after flow completes
   */
  setReturnUrl: (url: string) => sessionStorage.setItem(VezgoStorageKeys.RETURN_URL, url),
  
  /**
   * Get stored return URL
   */
  getReturnUrl: () => sessionStorage.getItem(VezgoStorageKeys.RETURN_URL),
  
  /**
   * Clear stored return URL
   */
  clearReturnUrl: () => sessionStorage.removeItem(VezgoStorageKeys.RETURN_URL),
  
  /**
   * Store provider information
   */
  setProvider: (provider: string) => sessionStorage.setItem(VezgoStorageKeys.PROVIDER, provider),
  
  /**
   * Get stored provider information
   */
  getProvider: () => sessionStorage.getItem(VezgoStorageKeys.PROVIDER),
  
  /**
   * Clear stored provider information
   */
  clearProvider: () => sessionStorage.removeItem(VezgoStorageKeys.PROVIDER),
  
  /**
   * Store previous path for navigation
   */
  setPreviousPath: (path: string) => sessionStorage.setItem(VezgoStorageKeys.PREVIOUS_PATH, path),
  
  /**
   * Get stored previous path
   */
  getPreviousPath: () => sessionStorage.getItem(VezgoStorageKeys.PREVIOUS_PATH),
  
  /**
   * Clear stored previous path
   */
  clearPreviousPath: () => sessionStorage.removeItem(VezgoStorageKeys.PREVIOUS_PATH),
  
  /**
   * Callback processed
   */
  setCallbackProcessed: (flag: string) => sessionStorage.setItem(VezgoStorageKeys.CALLBACK_PROCESSED, flag),
  getCallbackProcessed: () => sessionStorage.getItem(VezgoStorageKeys.CALLBACK_PROCESSED),
  clearCallbackProcessed: () => sessionStorage.removeItem(VezgoStorageKeys.CALLBACK_PROCESSED),

  /**
   * Callback time
   */
  setCallbackTime: (time: string) => sessionStorage.setItem(VezgoStorageKeys.CALLBACK_TIME, time),
  getCallbackTime: () => sessionStorage.getItem(VezgoStorageKeys.CALLBACK_TIME),
  clearCallbackTime: () => sessionStorage.removeItem(VezgoStorageKeys.CALLBACK_TIME),

  /**
   * Callback provider
   */
  setCallbackProvider: (provider: string) => sessionStorage.setItem(VezgoStorageKeys.CALLBACK_PROVIDER, provider),
  getCallbackProvider: (): string | null => sessionStorage.getItem(VezgoStorageKeys.CALLBACK_PROVIDER),
  clearCallbackProvider: () => sessionStorage.removeItem(VezgoStorageKeys.CALLBACK_PROVIDER),

  /**
   * Callback connection ID
   */
  setCallbackConnectionId: (id: string) => sessionStorage.setItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID, id),
  getCallbackConnectionId: () => sessionStorage.getItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID),
  clearCallbackConnectionId: () => sessionStorage.removeItem(VezgoStorageKeys.CALLBACK_CONNECTION_ID),

  /**
   * Clear all Vezgo-related storage items
   */
  clearAll: () => {
    Object.values(VezgoStorageKeys).forEach(key => {
      sessionStorage.removeItem(key);
    });
  }
};

/**
 * Storage Strategy Documentation
 * 
 * 1. Key Management:
 *    - All storage keys are centralized in VezgoStorageKeys enum
 *    - Keys use 'vezgo_' prefix to prevent naming conflicts
 *    - Each key serves a specific, documented purpose
 * 
 * 2. Access Patterns:
 *    - Always use vezgoStorage utility for storage operations
 *    - Direct sessionStorage access is discouraged
 *    - Prefer context state over storage when possible
 * 
 * 3. Lifecycle:
 *    - Connection flow data is temporary, cleared after completion
 *    - Error states persist until explicitly cleared
 *    - Token management follows OAuth best practices
 * 
 * 4. Migration Path:
 *    - Legacy components are being updated to use vezgoStorage
 *    - Direct sessionStorage access is being phased out
 *    - Context-based state management is preferred
 */
